import { Button } from "@/components/ui/button"
import { Bot, Globe, Users, MessageCircle, BarChart2, Calendar } from 'lucide-react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react';
import CoraaAIDialog from '@/components/givf/CoraaAIDialog';
import { OptimizedImage } from "@/components/ui/optimized-image"
import { SectionWrapper } from "@/components/ui/section-wrapper"
import { FeatureCard } from "@/components/ui/feature-card"
import { useSectionAnimation } from "@/hooks/use-section-animation"

export const AIAssistantSection = ({ reverse = false }: { reverse?: boolean }) => {
  const navigate = useNavigate();
  const [isCoraaDialogOpen, setIsCoraaDialogOpen] = useState(false);
  const imageAnimation = useSectionAnimation({ direction: 'right' });

  const features = [
    {
      icon: MessageCircle,
      title: "Ask Anything. Get Strategic Answers.",
      description: "How do our CSR goals compare to peers?\nWhat NPOs work in climate + Mumbai?\nWhat’s a great observance-aligned campaign for June?",
    },
    {
      icon: Bot,
      title: "Upload Reports, Get Instant Analysis",
      description: "Drop in your CSR policy, ESG framework, or partner deck , CORAA summarizes, benchmarks, and recommends.",
    },
    {
      icon: BarChart2,
      title: "AI-Enhanced Analysis",
      description: "CORAA learns from your data and industry trends to deliver contextual answers, comparisons, and recommendations."
    },
  ];

  return (
    <>
      <SectionWrapper background="light">
      <div className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${reverse ? 'flex-row-reverse' : ''}`}>
        {/* Content Side */}
        <div className="order-2 lg:order-1">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-5 tracking-tight">
            CORAA Chat: Your 24/7 Strategy Copilot for CSR & Volunteering
          </h2>
          <div className="text-lg md:text-xl font-semibold text-green-700 mb-3 md:mb-4 leading-snug">
            Your always-on, AI-powered analyst for CSR, ESG, and volunteering strategy.
          </div>
          <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed">
            Ask questions. Compare strategies. Upload documents. CORAA responds instantly.
          </p>


          <div className="space-y-4 md:space-y-6 mb-8 md:mb-10">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                delay={index * 0.1}
              />
            ))}
          </div>

          <Button 
            className="bg-[#17863d] hover:bg-[#146c31] text-white font-bold text-lg px-8 py-4 rounded-lg transition-all duration-200 w-full max-w-xl mx-auto"
            onClick={() => setIsCoraaDialogOpen(true)}
          >
            Talk to CORAA , Your Impact Copilot Awaits
          </Button>
        </div>

        {/* Visual Side: Mascot + Animated Chat */}
        <motion.div 
          ref={imageAnimation.ref}
          className="order-1 lg:order-2 flex flex-col items-center justify-center h-full min-h-[420px]"
          initial={imageAnimation.initial}
          animate={imageAnimation.animate}
          transition={imageAnimation.transition}
        >
          <div className="flex flex-col items-center gap-4">
            <img
              src="/images/coraa-mascot.png"
              alt="CORAA Mascot"
              className="w-32 h-32 md:w-36 md:h-36 rounded-full border-4 border-[#e0f2f1] bg-[#f8fafc] shadow object-cover"
              style={{ marginBottom: 8 }}
            />
            <div className="w-[420px] max-w-full bg-white rounded-2xl shadow-xl border border-[#e0f2f1] overflow-hidden min-h-[20px]">
              <div className="flex items-center gap-2 bg-[#e8f5e9] px-4 py-2 border-b border-[#e0f2f1]">
                <span className="w-6 h-6 rounded-full bg-[#2E7D32] flex items-center justify-center text-white font-bold">C</span>
                <span className="font-semibold text-[#2E7D32]">CORAA</span>
                <span className="text-xs bg-[#2E7D32] text-white px-2 py-1 rounded ml-auto">AI</span>
              </div>
              <div className="relative h-44 md:h-48 overflow-hidden bg-[#f8fafc]">
                <div
                  className="absolute left-0 top-0 w-full"
                  style={{
                    height: '200%',
                    animation: 'verticalScroll 20s linear infinite',
                  }}
                >
                  <div className="flex flex-col gap-2 px-4 py-2 text-sm text-gray-800">
                    {[
                      "How do our CSR goals compare to peers?",
                      "What NPOs work in climate + Mumbai?",
                      "What’s a great observance-aligned campaign for June?",
                      "Upload our ESG report for instant analysis",
                      "Suggest SDG-aligned volunteering for Q3",
                      "Summarize our last quarter’s impact",
                      "Find DEI partners in Bangalore",
                      "Show trending campaigns for World Environment Day",
                      "Generate a benchmarking report vs IT sector",
                      "Suggest low-cost virtual events for remote teams",
                      "Analyze our CSR spend by region",
                      "What are top NPOs for skill development?",
                      "What’s new in ESG compliance this year?",
                      "Suggest high-impact city events next week",
                      "Find partners for a coastal clean-up in Chennai",
                      "How do FMCGs celebrate Independence Day?",
                    ].map((text, i) => (
                      <div key={i} className="bg-white rounded-lg px-3 py-2 shadow w-fit">
                        “{text}”
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <style>{`
            @keyframes verticalScroll {
              0% { transform: translateY(0%); }
              100% { transform: translateY(-50%); }
            }
          `}</style>
        </motion.div>
      </div>
    </SectionWrapper>
      <CoraaAIDialog
        open={isCoraaDialogOpen}
        initialInput="Let’s talk about CSR, ESG, or volunteering strategy"
        onClose={() => setIsCoraaDialogOpen(false)}
      />
    </>
  );
};
